/**
 * "What your AI reads" — the workspace standards the MCP server serves to every
 * connected assistant (Tech Stack, Checks, Model Settings, Rules, Prompt
 * Templates). It lives on the Docs home (/docs); each card links to the
 * standard's editor under /docs, where StandardsLayoutPage's tab strip also
 * lists them all. Data comes from the shared `standardsSections` SSOT so this
 * and the Learn-page recommendations stay in step. The shared `DocCardSection`
 * (sdx#1120) renders the grid — the same one the Docs-home Checks block uses.
 */

import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DocCardSection, type DocCardItem, type LinkComponent } from '@systemdox/ui';
import { standardsSections } from './sections';

export function WhatYourAiReadsSection() {
  // Carry the query string across the in-SPA link — on localhost the ?tenant
  // param IS the host selection, and dropping it would flip the app to the
  // neutral host mid-session. Empty in production. Memoised per search so the
  // cards don't remount each render. The injection keeps @systemdox/ui router-free.
  const { search } = useLocation();
  const link: LinkComponent = useMemo(() => {
    const DocsLink: LinkComponent = ({ to, className, title, children }) => (
      <Link to={{ pathname: to, search }} className={className} title={title}>{children}</Link>
    );
    return DocsLink;
  }, [search]);

  const items: DocCardItem[] = standardsSections.map((s) => ({
    key: s.key,
    icon: s.icon,
    iconBg: s.iconBg,
    iconColor: s.iconColor,
    title: s.title,
    description: s.description,
    to: s.to,
  }));

  return (
    <DocCardSection
      heading="Standards"
      hint="Served to every assistant over MCP"
      items={items}
      link={link}
    />
  );
}
